import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ShieldOff, KeyRound, Eraser, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultBox = ({ id, result }) => {
  if (!result) return null;
  return (
    <div data-testid={`${id}-result`} className={`mt-4 rounded-md border p-3 text-sm ${result.error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
      <p className="font-medium">{result.message}</p>
      {result.changes?.length > 0 && (
        <ul className="mt-2 list-disc pl-5 space-y-1">
          {result.changes.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </div>
  );
};

const MigrationCard = ({ id, icon: Icon, title, description, warning, endpoint, getBody, disabled, children }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}${endpoint}`, getBody ? getBody() : {});
      setResult(res.data);
      toast.success(res.data.message);
    } catch (e) {
      const msg = e.response?.data?.detail || "Migration failed";
      setResult({ error: true, message: typeof msg === "string" ? msg : JSON.stringify(msg) });
      toast.error(typeof msg === "string" ? msg : "Migration failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card data-testid={`${id}-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Icon className="h-5 w-5" />{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {children}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid={`${id}-run-btn`} disabled={running || disabled} variant="outline">
              {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running...</> : "Run"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{title}?</AlertDialogTitle>
              <AlertDialogDescription>{warning}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${id}-cancel-btn`}>Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid={`${id}-confirm-btn`} onClick={run}>Yes, run it</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <ResultBox id={id} result={result} />
      </CardContent>
    </Card>
  );
};

export const MaintenancePanel = () => {
  const [oldKey, setOldKey] = useState("");

  return (
    <div className="space-y-6" data-testid="maintenance-panel">
      <p className="text-sm text-slate-600">
        One-time database migrations. These run against the database of the site you are logged into (run them on your live site to update production).
      </p>
      <MigrationCard
        id="reset-mfa"
        icon={ShieldOff}
        title="Reset MFA for All Users"
        description="Clears MFA for every user (including you) so everyone re-enrolls with the new MFA encryption key on next login."
        warning="Every user, including you, will be required to set up MFA again on their next login. This cannot be undone."
        endpoint="/admin/reset-all-mfa"
      />
      <MigrationCard
        id="rotate-key"
        icon={KeyRound}
        title="Rotate Financial Encryption Key"
        description="Re-encrypts stored financial data from the old FINANCIAL_ENCRYPTION_KEY to the new one currently configured."
        warning="Encrypted financial data will be decrypted with the old key you entered and re-encrypted with the new key."
        endpoint="/admin/rotate-financial-key"
        getBody={() => ({ old_key: oldKey.trim() })}
        disabled={!oldKey.trim()}
      >
        <div className="mb-4 space-y-2">
          <Label htmlFor="old-key">Old FINANCIAL_ENCRYPTION_KEY</Label>
          <Input id="old-key" data-testid="rotate-key-old-key-input" type="password" value={oldKey}
            onChange={(e) => setOldKey(e.target.value)} placeholder="Paste the previous key" />
        </div>
      </MigrationCard>
      <MigrationCard
        id="cleanup-suffixes"
        icon={Eraser}
        title="Clean Up Duplicate Column Suffixes"
        description="Fixes old line items like 'otc.1' (renamed to 'otc cog' as COGS) and removes other '.1' style suffixes."
        warning="Line item names with '.1', '.2' suffixes will be renamed in the P&L and YTD data."
        endpoint="/admin/cleanup-pandas-suffixes"
      />
    </div>
  );
};
